/* This script creates charts based on the provided data and chart types using Chart.js library. */

/**
 * The 'createCharts' function takes an options object as a parameter and generates the charts based on the specified options.
 * First it creates a div element and appends it to the correct place of the DOM, then draws a <canvas> inside of the said div.
 * @param opts - options from the px3:graph tag from JSP
 */
export function createCharts (opts) {
	/* Chart types */
	const DISCRETE_BAR_CHART = "discreteBarChart";
	const MULTI_BAR_CHART = "multiBarChart";
	const PIE_CHART = "pieChart";
	const STACKED_AREA_CHART = "stackedAreaChart";
	const LINE_CHART = "lineChart";
	const CUMULATIVE_LINE_CHART = "cumulativeLineChart";
	const MULTIBAR_STACKED_CHART = "stackedMultiBarChart";

	const noDataMessage = Ptx.getMsg("evaluationOfDriver.statistics.noData");
	let chartData = opts.data.charts;

	/* Global chart setup */
	for (var i = 0; i < chartData.length; i++) {
		let chartOptions = chartData[i];
		let chartType = chartOptions.type;
		chartOptions.dateFrom = opts.dateFrom;
		chartOptions.dateTo = opts.dateTo;

		let randomId = Math.random().toString(36).substring(7);

		let $chartContainer = $("<div></div>").attr("id", "chart-" + randomId).css("height", "500px").addClass(chartOptions.cssClass + " p-bottom-15");
		$('#charts').append($chartContainer);

		let chartId = "chart-canvas-" + randomId;
		let $chartCanvas = $("<canvas></canvas>").attr("id", chartId);

		$chartContainer.append($chartCanvas);

		Chart.defaults.animation.duration = Ptx.isHeadlessChromeAccess ? 0 : 1000;

		/* Check if there is data and build charts accordingly */
		let allDataEmpty = chartOptions.data.every(function(dataItem) {
			return dataItem.values.length === 0;
		});

		if (allDataEmpty) {
			$chartContainer.css("height", "200px").html("<div class='text-center top-50'><h4>" + chartOptions.title + "</h4>" + noDataMessage + "</div>");
		} else {
			switch (chartType) {
				case DISCRETE_BAR_CHART:
					createDiscreteBarChart($chartCanvas, chartOptions);
					break;
				case MULTI_BAR_CHART:
					createMultiBarRegularChart($chartCanvas, chartOptions);
					break;
				case PIE_CHART:
					createPieChart($chartCanvas, chartOptions);
					break;
				case STACKED_AREA_CHART:
					createStackedAreaChart($chartCanvas, chartOptions);
					break;
				case LINE_CHART:
					createLineChart($chartCanvas, chartOptions, false);
					break;
				case CUMULATIVE_LINE_CHART:
					createLineChart($chartCanvas, chartOptions, true);
					break;
				case MULTIBAR_STACKED_CHART:
					createMultiBarStackedChart($chartCanvas, chartOptions);
					break;
			}
		}
	}
}

function createDiscreteBarChart(chartCanvas, chartOptions) {
	let chartData = chartOptions.data[0].values;
	let labels = chartData.map(function (d) {
		return d.label;
	});
	let values = chartData.map(function (d) {
		return d.value;
	});

	let chartConfig = {
		type: "bar",
		data: {
			labels: labels,
			datasets: [
				{
					label: chartOptions.title,
					data: values,
					backgroundColor: "rgba(0, 123, 255, 0.7)",
				},
			],
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			scales: {
				x: {
					grid: {
						display: true
					},
				},
				y: {
					grid: {
						display: true,
					},
					ticks: {
						beginAtZero: true,
					},
				},
			},
		},
	};

	return new Chart(chartCanvas[0].getContext("2d"), chartConfig);
}

function createMultiBarChart(chartCanvas, chartOptions, stacked) {
	let chartData = chartOptions.data;
	let labels = chartData[0].values.map(function (d) {
		return moment(d.label).tz(Ptx.timeZoneId).format("L LT");
	});
	let datasets = chartData.map(function (dataset) {
		return {
			label: dataset.key,
			data: dataset.values.map(function (d) {
				return d.value;
			}),
			backgroundColor: Chart.helpers.color(dataset.color).alpha(0.3).rgbString(),
			borderColor: dataset.color,
			borderWidth: 1
		};
	});

	let annotations = [];

	const $speedLimit = $("#sp\\.speedLimit");
	let subtitleText = "";

	if ($speedLimit.length > 0 && $speedLimit.val() > 0) {
		subtitleText = Ptx.getMsg("common.filter.SPEED_LIMIT") + ": " + $speedLimit.val();
		annotations.push({
			type: 'line',
			scaleID: 'y',
			value: $speedLimit.val(),
			borderColor: 'red',
			borderWidth: 2,
		});
	}

	let chartConfig = {
		type: "bar",
		data: {
			labels: labels,
			datasets: datasets,
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				title: {
					display: true,
					fullSize: true,
					text: chartOptions.title,
				},
				subtitle: {
					display: subtitleText.length > 0,
					text: subtitleText,
				},
				legend: {
					fullSize: true,
				},
				tooltip: {
					callbacks: {
						label: function(value) {
							return value.dataset.label + ": " + Number(value.parsed.y.toFixed(6)) + " " + chartOptions.yAxis.unit;
						}
					}
				},
				annotation: {
					annotations: annotations
				}
			},
			scales: {
				x: {
					stacked: stacked,
					grid: {
						display: true,
					},
				},
				y: {
					stacked: stacked,
					grid: {
						display: true,
					},
					ticks: {
						beginAtZero: true,
						callback: function(value) {
							return value + " " + chartOptions.yAxis.unit;
						},
					},
				},
			},
		},
	};
	return new Chart(chartCanvas[0].getContext("2d"), chartConfig);
}

/* Functions to create MultiBar charts depending on if they are stacked or not (stacked attribute) */
function createMultiBarRegularChart(chartCanvas, chartOptions) {
	return createMultiBarChart(chartCanvas, chartOptions, false);
}

function createMultiBarStackedChart(chartCanvas, chartOptions) {
	return createMultiBarChart(chartCanvas, chartOptions, true);
}

function createPieChart(chartCanvas, chartOptions) {
	let chartData = chartOptions.data[0].values;
	let labels = chartData.map(function (d) {
		return d.label;
	});
	let values = chartData.map(function (d) {
		return d.value;
	});
	let chartConfig = {
		type: "doughnut",
		data: {
			labels: labels,
			datasets: [
				{
					data: values,
					backgroundColor: chartOptions.colors,

				},
			],
		},
		options: {
			plugins: {
				title: {
					display: true,
					text: chartOptions.title,
				},
				tooltip: {
					callbacks: {
						label: function(values) {
							return parseFloat((values.parsed).toFixed(3)) + " " + chartOptions.yAxis.unit;
						}
					}
				},
			},
			responsive: true,
			maintainAspectRatio: false,
		},
	};
	return new Chart(chartCanvas[0].getContext("2d"), chartConfig);
}

function createStackedAreaChart(chartCanvas, chartOptions) {
	let chartData = chartOptions.data;
	let labels = chartData[0].values.map(function (d) {
		return d.label;
	});
	let datasets = chartData.map(function (dataset) {
		return {
			label: dataset.key,
			data: dataset.values.map(function (d) {
				return d.value;
			}),
			backgroundColor: dataset.color,
			fill: true,
		};
	});

	let chartConfig = {
		type: "line",
		data: {
			labels: labels,
			datasets: datasets,
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			scales: {
				x: {
					grid: {
						display: true,
					},
				},
				y: {
					grid: {
						display: true,
					},
					ticks: {
						beginAtZero: true,
					},
				},
			},
		},
	};
	return new Chart(chartCanvas[0].getContext("2d"), chartConfig);
}

function createLineChart(chartCanvas, chartOptions, cumulative) {
	const ctx = chartCanvas[0].getContext("2d");
	const chartData = chartOptions.data;
	const datasets = mergeDatasets(chartOptions);
	const labels = datasets.labels;

	const fromDate = new Date(chartOptions.dateFrom).getTime();
	const toDate = new Date(chartOptions.dateTo).getTime();

	const chartConfig = {
		type: "line",
		data: {
			labels: datasets.labels,
			datasets: datasets.datasets,
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			interaction: {
				intersect: false,
				axis: 'x',
				mode: 'index',
			},
			elements: {
				point: {
					radius: 0,
				},
			},
			onHover: function (event, chartElements) {
				if (chartElements.length > 0 && datasets.datasets[0].description[0]) {
					ctx.canvas.style.cursor = "pointer";
				} else {
					ctx.canvas.style.cursor = "default";
				}
			},
			onClick: function (event, chartElements) {
				// Functionality is specific only for modal dialog in logbook!
				if (Ptx.modal.ctx && Ptx.modal.ctx.map && chartElements.length > 0 && datasets.datasets[0].description[0]) {
					var clickedIndex = chartElements[0].index;
					var chartData = datasets.datasets[0].description;
					setOnPointClick(clickedIndex, chartData);
				}
			},
			plugins: {
				title: {
					display: true,
					fullSize: false,
					text: chartData[0].key,
				},
				legend: {
					onClick(evt, item) {
						if (datasets.length === 1) {
							annotationsToggle = !annotationsToggle;

							const annotationObjects = Object.values(chartConfig.options.annotations);

							annotationObjects.forEach(annotation => {
								annotation.display = annotationsToggle;
							});
						}

						Chart.defaults.plugins.legend.onClick.call(this, evt, item, this);
					},
					fullSize: false,
				},
				tooltip: {
					callbacks: {
						label: function (value) {
							return (value.dataset.label +
									": " +
									Number(value.parsed.y.toFixed(2)) +
									" " +
									chartOptions.yAxis.unit
							);
						},
					},
				},
				zoom: {
					pan: {
						enabled: Ptx.fn.isDesktop(),
						mode: "x",
					},
					limits: {
						x: {
							min: cumulative ? fromDate : 'original',
							max: cumulative ? toDate : 'original',
						}
					},
					zoom: {
						wheel: {
							enabled: true,
							modifierKey: "ctrl",
						},
						mode: "x",
					},
				},
			},
			scales: {
				x: {
					type: cumulative ? 'time' : 'linear',
					time: {
						tooltipFormat: 'L LT',
					},
					ticks: {
						maxTicksLimit: 12,
						callback: value => cumulative ? moment(value).format('L LT') : value + " " + chartOptions.xAxis.unit
					},
					grid: {
						display: true,
					},
					min: cumulative ? fromDate : 'original',
					max: cumulative ? toDate : 'original',
				},
				y: {
					grace: (maxValue(ctx) - minValue(ctx)) * 0.1,
					grid: {
						display: true,
					},
					ticks: {
						beginAtZero: true,
						callback: function (value) {
							return Number(value.toFixed(2)) +
									" " +
									chartOptions.yAxis.unit
						},
					},
					min: chartOptions.yAxis?.min,
					max: chartOptions.yAxis?.max,
				},
			},
		},
	};

	if (datasets.datasets.length === 1) {
		const annotationsToggle = true;
		const averageValueY = calculateAverage(datasets.datasets[0].data);
		chartConfig.options.annotations = {
			averageLine: {
				type: "line",
				mode: "horizontal",
				scaleID: "y",
				value: averageValueY,
				borderColor: Chart.helpers.color(chartData[0].color)
						.alpha(0.6)
						.rgbString(),
				borderDash: [6, 6],
				borderDashOffset: 0,
				borderWidth: 2,
				display: datasets.datasets[0].data.length === 0 ? false : annotationsToggle,
			},
			averageValueLabel: {
				type: 'label',
				yValue: averageValueY,
				xValue: ((labels[labels.length - 1]) - labels[0]) / 2 + labels[0],
				content: Ptx.getMsg("statistics.avgValue") +
						": " +
						parseFloat(averageValueY.toFixed(3)),
				color: Chart.helpers.color('white').rgbString(),
				backgroundColor: Chart.helpers.color(datasets.datasets[0].borderColor).alpha(0.45).rgbString(),
				borderColor: datasets.datasets[0].borderColor,
				borderRadius: 10,
				font: {
					size: 14,
					weight: 'bold',
				},
				display: datasets.datasets[0].data.length === 0 ? false : annotationsToggle,
			},
			maxValueLabel: {
				type: 'label',
				xValue: (ctx) => maxIndex(ctx),
				yValue: (ctx) => maxValue(ctx),
				yAdjust: -25,
				position: {
					x: (ctx) => {
						const totalTimeRange = labels[labels.length - 1] - labels[0];
						const tenPercent = totalTimeRange * 0.1;
						const timeDiffFromStart = maxIndex(ctx) - labels[0];
						const timeDiffFromEnd = labels[labels.length - 1] - maxIndex(ctx);

						if (timeDiffFromStart <= tenPercent || timeDiffFromEnd <= tenPercent) {
							return timeDiffFromStart <= tenPercent ? 'start' : 'end';
						}
						return 'center';
					},
				},
				color: Chart.helpers.color('black').alpha(0.7).rgbString(),
				content: Ptx.getMsg("statistics.maxValue") + ": "
				+ parseFloat(maxValue(ctx).toFixed(3)) + " "
				+ chartOptions.yAxis.unit,
				font: {
					size: 14
				},
				display: annotationsToggle,
			},
			maxValuePoint: {
				type: 'point',
				backgroundColor: Chart.helpers.color(datasets.datasets[0].borderColor).alpha(0.4).rgbString(),
				borderColor: datasets.datasets[0].borderColor,
				pointStyle: 'rectRounded',
				radius: 10,
				xValue: (ctx) => maxIndex(ctx),
				yValue: (ctx) => maxValue(ctx),
				display: annotationsToggle,
			},
			minValueLabel: {
				type: 'label',
				xValue: (ctx) => minIndex(ctx),
				yValue: (ctx) => minValue(ctx),
				yAdjust: 25,
				position: {
					x: (ctx) => {
						const totalTimeRange = labels[labels.length - 1] - labels[0];
						const tenPercent = totalTimeRange * 0.1;
						const timeDiffFromStart = minIndex(ctx) - labels[0];
						const timeDiffFromEnd = labels[labels.length - 1] - minIndex(ctx);

						if (timeDiffFromStart <= tenPercent || timeDiffFromEnd <= tenPercent) {
							return timeDiffFromStart <= tenPercent ? 'start' : 'end';
						}

						return 'center';
					},
				},
				color: Chart.helpers.color('black').alpha(0.7).rgbString(),
				content: Ptx.getMsg("statistics.minValue") + ": "
						+ parseFloat(minValue(ctx).toFixed(3)) + " "
						+ chartOptions.yAxis.unit,
				font: {
					size: 14
				},
				display: annotationsToggle,
			},
			minValuePoint: {
				type: 'point',
				backgroundColor: Chart.helpers.color(datasets.datasets[0].borderColor).alpha(0.4).rgbString(),
				borderColor: datasets.datasets[0].borderColor,
				pointStyle: 'rectRounded',
				radius: 10,
				xValue: (ctx) => minIndex(ctx),
				yValue: (ctx) => minValue(ctx),
				display: annotationsToggle,
			},
		};
	}
	function maxValue(ctx) {
		let maxResult = -Infinity;
		const dataset = datasets.datasets;
		dataset.forEach(function (el) {
			const values = el.data
				.filter(function(d){return d != null;});
			var max = Math.max(...values);
			if (max > maxResult) {
				maxResult = max;
			}
		});
		return maxResult;
	}

	function minValue(ctx) {
		let minResult = Infinity;
		const dataset = datasets.datasets;
		dataset.forEach(function (el) {
			const values = el.data
				.filter(function(d){return d != null;});
			var min = Math.min(...values);
			if (min < minResult) {
				minResult = min;
			}
		});
		return minResult;
	}

	function maxIndex(ctx) {
		const max = maxValue(ctx);
		const dataset = ctx.chart.data.datasets[0];
		const maxIndex = dataset.data.indexOf(max);
		return labels[maxIndex];
	}

	function minIndex(ctx) {
		const min = minValue(ctx);
		const dataset = ctx.chart.data.datasets[0];
		const minIndex = dataset.data.indexOf(min);
		return labels[minIndex];
	}
	return new Chart(ctx, chartConfig);
}

function mergeDatasets(chartOptions) {
	// Initialize an empty merged dataset object
	const mergedDataset = {
		labels: [], // Array to store unique labels
		datasets: [] // Array to store merged values
	};

	const datasets = chartOptions.data;

	// Iterate over each dataset
	for (var i = 0; i < datasets.length; i++) {
		const dataset = datasets[i];

		// Merge labels
		mergedDataset.labels = mergedDataset.labels.concat(
			dataset.values
			.map(function (value) {
				return value.label;
			})
			.filter(function(label) {
				return mergedDataset.labels.indexOf(label) === -1; // Filter out duplicate labels
		}));

		// Merge dataSets
		const mergedData = new Array(mergedDataset.labels.length).fill(null);
		// Fill in data for existing labels
		dataset.values.forEach(function(record, index) {
			const position = mergedDataset.labels.indexOf(record.label);
			if (position !== -1) {
				mergedData[position] = dataset.values[index].value;
			}
		});
		// Add dataset to merged dataset
		mergedDataset.datasets.push({
			label: chartOptions.unitName
				? `${dataset.key} (${chartOptions.unitName})`
				: dataset.key,
			data: mergedData,
			description: dataset.values.map(function (d) {
				return d.description;
			}),
			fill: true,
			backgroundColor: Chart.helpers.color(dataset.color).alpha(0.1).rgbString(),
			borderColor: dataset.color,
			borderWidth: 2,
			tension: 0.02
		});
	}

	return mergedDataset;
}

function customTickFormatting(value) {
	const numString = value.toString();
	const decimalIndex = numString.indexOf('.');
	if (decimalIndex !== -1 && numString.length - decimalIndex - 1 >= 13) {
		return Math.round(value);
	} else {
		return value;
	}
}

function calculateAverage(data) {
	var sum = data?.reduce(function (a, b) {
		return a + b;
	}, 0);
	return sum / data.length;
}

/**
 * 'setOnPointClick' is used for map interaction where a temporary POI gets created when user clicks on a corresponding graph index.
 * @param clickedIndex - the index of the clicked element
 * @param chartData - chart description data
 */

function setOnPointClick(clickedIndex, chartData) {
	var description = chartData[clickedIndex];
	var descriptionElement = $('*[id^="graph"].ptx-render-graph').first();
	description.objectType = descriptionElement.data('object-type');
	description.color = descriptionElement.data('unit-color');
	Ptx.modal.ctx.map.createPoint(description);
}
